import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { EquityPoint, MonthlyReturn } from '../data/demo'

/* ---------- Modal ---------- */
type ModalCtx = {
  open: (node: ReactNode, opts?: { title?: string; labelledBy?: string }) => void
  close: () => void
}

const ModalContext = createContext<ModalCtx | null>(null)

export function ModalProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<ReactNode>(null)
  const [title, setTitle] = useState<string | undefined>()
  const titleId = useId()
  const closeBtn = useRef<HTMLButtonElement>(null)

  const close = useCallback(() => setContent(null), [])
  const open = useCallback((node: ReactNode, opts?: { title?: string }) => {
    setTitle(opts?.title)
    setContent(node)
  }, [])

  useEffect(() => {
    if (!content) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeBtn.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [content, close])

  const value = useMemo(() => ({ open, close }), [open, close])

  return (
    <ModalContext.Provider value={value}>
      {children}
      {content
        ? createPortal(
            <div
              className="modal-backdrop"
              role="presentation"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) close()
              }}
            >
              <div
                className="modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
              >
                <div className="modal-head">
                  <div>
                    {title ? <h2 id={titleId}>{title}</h2> : null}
                  </div>
                  <button ref={closeBtn} type="button" className="btn btn-ghost btn-sm" onClick={close} aria-label="Close dialog">
                    Close
                  </button>
                </div>
                {content}
              </div>
            </div>,
            document.body,
          )
        : null}
    </ModalContext.Provider>
  )
}

export function useModal() {
  const ctx = useContext(ModalContext)
  if (!ctx) throw new Error('useModal requires ModalProvider')
  return ctx
}

/* ---------- Early access ---------- */
export function EarlyAccessForm({ onDone }: { onDone?: () => void }) {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        setSent(true)
        onDone?.()
      }}
    >
      <p className="muted" style={{ marginTop: 0 }}>
        Billing is inactive on this development build. Leave an email to simulate early-access interest — nothing is
        stored remotely.
      </p>
      {sent ? (
        <div className="notice ok" role="status">
          Captured locally in this browser session only. No network call was made.
        </div>
      ) : (
        <div className="stack" style={{ gap: '0.75rem' }}>
          <label>
            <span className="sr-only">Email</span>
            <input
              className="input"
              type="email"
              required
              placeholder="you@desk.example"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </label>
          <button type="submit" className="btn btn-primary">
            Join early-access list (local only)
          </button>
        </div>
      )}
    </form>
  )
}

/* ---------- Connect account ---------- */
export function ConnectAccountPanel({ onClose }: { onClose?: () => void }) {
  const [method, setMethod] = useState<'ea' | 'managed' | 'import'>('ea')
  return (
    <div className="stack">
      <p className="muted" style={{ marginTop: 0 }}>
        Atlas never asks for trading passwords or investor passwords in this demo. Connection concepts are
        read-only by design.
      </p>
      <div className="tabs" role="tablist" aria-label="Connection method">
        {(
          [
            ['ea', 'Reporter EA'],
            ['managed', 'Managed connector'],
            ['import', 'Statement import'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            className="tab"
            aria-selected={method === id}
            onClick={() => setMethod(id)}
          >
            {label}
          </button>
        ))}
      </div>
      {method === 'ea' && (
        <div className="notice ok">
          <strong>Recommended — NikaQuant Reporter EA.</strong>
          <p style={{ margin: '0.5rem 0 0' }}>
            Self-owned read-only push from your terminal. You control the outbound feed. Atlas stores performance
            digests, not credentials. Attach the EA, copy the site token, confirm heartbeat.
          </p>
          <ol>
            <li>Install Reporter EA on a chart (hedging or netting).</li>
            <li>Paste one-time device token from Atlas.</li>
            <li>Verify first snapshot — balance, equity, open risk.</li>
          </ol>
          <button type="button" className="btn btn-primary btn-sm" onClick={onClose}>
            Simulate successful EA link
          </button>
        </div>
      )}
      {method === 'managed' && (
        <div className="notice warn">
          <strong>Managed read-only connector — concept.</strong>
          <p style={{ margin: '0.5rem 0 0' }}>
            Future broker / MetaApi-style bridge. Would use broker-issued read-only scopes only. Not active in this
            development build — no OAuth, no password fields.
          </p>
          <button type="button" className="btn btn-secondary btn-sm" disabled>
            Connector waitlist (inactive)
          </button>
        </div>
      )}
      {method === 'import' && (
        <div className="notice">
          <strong>Statement import — CSV / HTML concept.</strong>
          <p style={{ margin: '0.5rem 0 0' }}>
            Drop a broker statement for offline reconstruction. Imports are labeled unverified until a live read-only
            feed confirms the chain. No files leave this browser in the demo.
          </p>
          <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
            Choose statement file
            <input
              type="file"
              accept=".csv,.html,.htm,.xlsx"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) alert(`Demo only: “${f.name}” would be parsed locally. No upload performed.`)
              }}
            />
          </label>
        </div>
      )}
    </div>
  )
}

/* ---------- Charts ---------- */
export function EquityChart({
  data,
  height = 220,
  showBalance = true,
}: {
  data: EquityPoint[]
  height?: number
  showBalance?: boolean
}) {
  const sliced = data.length > 180 ? data.filter((_, i) => i % 2 === 0) : data
  return (
    <div style={{ width: '100%', height }} role="img" aria-label="Equity and balance chart">
      <ResponsiveContainer>
        <AreaChart data={sliced} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="eqFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#b8ff3c" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#b8ff3c" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="rgba(242,244,240,0.06)" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fill: '#6b737c', fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            minTickGap={40}
          />
          <YAxis
            tick={{ fill: '#6b737c', fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={56}
            domain={['auto', 'auto']}
          />
          <Tooltip
            contentStyle={{
              background: '#12151a',
              border: '1px solid rgba(242,244,240,0.12)',
              borderRadius: 10,
              fontFamily: 'IBM Plex Mono, monospace',
              fontSize: 12,
            }}
          />
          <Area
            type="monotone"
            dataKey="equity"
            name="Equity"
            stroke="#b8ff3c"
            fill="url(#eqFill)"
            strokeWidth={2}
            dot={false}
            isAnimationActive={
              typeof window !== 'undefined'
                ? !window.matchMedia('(prefers-reduced-motion: reduce)').matches
                : false
            }
          />
          {showBalance ? (
            <Area
              type="monotone"
              dataKey="balance"
              name="Balance"
              stroke="#5ec8ff"
              fill="transparent"
              strokeWidth={1.5}
              strokeDasharray="4 4"
              dot={false}
              isAnimationActive={false}
            />
          ) : null}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

export function MonthlyHeatmap({ data }: { data: MonthlyReturn[] }) {
  const years = [...new Set(data.map((d) => d.year))].sort()
  const months = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']
  return (
    <div className="stack" style={{ gap: '0.5rem' }}>
      {years.map((y) => (
        <div key={y}>
          <div className="mute mono" style={{ fontSize: '0.75rem', marginBottom: 4 }}>
            {y}
          </div>
          <div className="heatmap" aria-label={`Monthly returns ${y}`}>
            {months.map((label, idx) => {
              const m = idx + 1
              const cell = data.find((d) => d.year === y && d.month === m)
              const ret = cell?.ret
              let bg = '#1f2530'
              let color = '#9aa3ad'
              if (ret != null) {
                if (ret >= 4) bg = '#b8ff3c'
                else if (ret >= 1.5) bg = '#8dcc30'
                else if (ret >= 0) bg = '#4d7020'
                else if (ret > -2) bg = '#703020'
                else bg = '#ff4d4d'
                color = ret >= 0 ? '#0a0b0d' : '#fff'
              }
              return (
                <div
                  key={m}
                  className="cell"
                  style={{ background: bg, color } satisfies CSSProperties}
                  title={ret == null ? `${label} ${y}: n/a` : `${label} ${y}: ${ret}%`}
                >
                  {ret == null ? '·' : `${ret > 0 ? '+' : ''}${Math.round(ret)}`}
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

export function AllocationBars({
  items,
}: {
  items: { label: string; pct: number; detail?: string; color?: string }[]
}) {
  return (
    <div className="bar-list">
      {items.map((it) => (
        <div className="bar-row" key={it.label}>
          <span>{it.label}</span>
          <div className="bar-track" aria-hidden>
            <div
              className="bar-fill"
              style={{ width: `${Math.min(100, Math.max(0, it.pct))}%`, background: it.color }}
            />
          </div>
          <span className="mono num muted">{it.detail ?? `${it.pct}%`}</span>
        </div>
      ))}
    </div>
  )
}

export function Metric({
  label,
  value,
  sub,
  tone,
}: {
  label: string
  value: string
  sub?: string
  tone?: 'lime' | 'danger' | 'amber' | ''
}) {
  return (
    <div className="metric">
      <div className="label">{label}</div>
      <div className={`value ${tone ?? ''}`}>{value}</div>
      {sub ? <div className="sub">{sub}</div> : null}
    </div>
  )
}

export function StateBlock({
  mode,
  onRetry,
}: {
  mode: 'loading' | 'empty' | 'error'
  onRetry?: () => void
}) {
  if (mode === 'loading') {
    return (
      <div className="load-state" role="status" aria-live="polite">
        <div className="skeleton" style={{ height: 14, width: '40%', margin: '0 auto 12px' }} />
        <div className="skeleton" style={{ height: 14, width: '70%', margin: '0 auto 12px' }} />
        <div className="skeleton" style={{ height: 14, width: '55%', margin: '0 auto' }} />
        <p>Refreshing verified feed…</p>
      </div>
    )
  }
  if (mode === 'empty') {
    return (
      <div className="empty-state">
        <p>
          <strong>No trades in this filter window.</strong>
        </p>
        <p className="mute">Widen dates or clear setup tags.</p>
      </div>
    )
  }
  return (
    <div className="fail-state" role="alert">
      <p>
        <strong>Feed interrupted.</strong>
      </p>
      <p className="mute">Demo failure state — checksum mismatch simulated.</p>
      {onRetry ? (
        <button type="button" className="btn btn-secondary btn-sm" onClick={onRetry}>
          Retry
        </button>
      ) : null}
    </div>
  )
}
